import ReactECharts from 'echarts-for-react';

/**
 * AuditTrailChart_4 — CONTENT alternative to AuditTrailChart (blog04).
 * Instead of the incident counts, this is a visual TIMELINE of the EU AI Act's
 * logging-obligation milestones (Regulation (EU) 2024/1689) — the "every action
 * leaves a receipt" principle becoming law. All dates are the regulation's real
 * application dates (Article 113); the record-keeping duty is Article 12.
 *
 * Sources:
 *   Art. 12 (record-keeping): https://artificialintelligenceact.eu/article/12/
 *   Art. 113 (entry into force / application dates): https://artificialintelligenceact.eu/article/113/
 *
 * Chart-type-first: discrete events over calendar time → ECharts time axis with
 * a track line and labeled milestone markers. Light editorial style; opaque.
 */

const PALETTE = {
  accent: '#0AB3BF',
  text: '#334155',
  textSoft: '#64748B',
  hairline: '#E2E8F0',
  surface: '#FFFFFF',
};

// Real application dates of Regulation (EU) 2024/1689 (Article 113).
const MILESTONES = [
  { date: '2025-02-02', label: '2 Feb 2025', title: 'Chapters I–II apply', note: 'prohibitions + AI literacy' },
  { date: '2025-08-02', label: '2 Aug 2025', title: 'GPAI obligations', note: 'general-purpose AI rules' },
  {
    date: '2026-08-02',
    label: '2 Aug 2026',
    title: 'Full application',
    note: 'incl. Art. 12 record-keeping for high-risk systems',
  },
  {
    date: '2027-08-02',
    label: '2 Aug 2027',
    title: 'Art. 6(1) systems',
    note: 'corresponding obligations apply',
  },
];

export const AuditTrailChart_4 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      trigger: 'item',
      backgroundColor: PALETTE.surface,
      borderColor: PALETTE.hairline,
      borderWidth: 1,
      textStyle: { color: PALETTE.text, fontSize: 12 },
      formatter: (p: { dataIndex: number }) => {
        const m = MILESTONES[p.dataIndex];
        return `<b>${m.label}</b> — ${m.title}<br/>${m.note}`;
      },
    },
    grid: { left: 32, right: 32, top: 40, bottom: 40 },
    xAxis: {
      type: 'time',
      min: '2024-11-01',
      max: '2028-02-01',
      axisLine: { lineStyle: { color: PALETTE.hairline, width: 1 } },
      axisLabel: { color: PALETTE.textSoft, fontSize: 11, formatter: '{MMM} {yyyy}' },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: { show: false, min: 0, max: 3 },
    series: [
      {
        // track line connecting the milestones
        type: 'line',
        data: MILESTONES.map((m) => [m.date, 1]),
        lineStyle: { color: PALETTE.accent, width: 1.5, type: 'dashed' },
        symbol: 'none',
        silent: true,
        z: 1,
      },
      {
        type: 'scatter',
        data: MILESTONES.map((m, i) => ({
          value: [m.date, 1],
          label: {
            position: i % 2 === 0 ? 'top' : 'bottom',
            distance: 14,
          },
        })),
        symbolSize: 12,
        itemStyle: { color: PALETTE.accent, borderColor: PALETTE.surface, borderWidth: 2 },
        label: {
          show: true,
          color: PALETTE.text,
          fontSize: 11,
          lineHeight: 15,
          width: 168,
          overflow: 'break',
          formatter: (p: { dataIndex: number }) => {
            const m = MILESTONES[p.dataIndex];
            return `{d|${m.label}}\n{t|${m.title}}\n{n|${m.note}}`;
          },
          rich: {
            d: { color: PALETTE.textSoft, fontSize: 11, fontWeight: 600, lineHeight: 16 },
            t: { color: PALETTE.text, fontSize: 12, fontWeight: 700, lineHeight: 17 },
            n: { color: PALETTE.textSoft, fontSize: 11, lineHeight: 15 },
          },
        },
        z: 2,
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-4">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. The EU AI Act phases in logging obligations on fixed legal dates — from February 2025 to full
        application in August 2026. Article 12: &ldquo;High-risk AI systems shall technically allow for the automatic
        recording of events (logs) over the lifetime of the system.&rdquo; In other words, &lsquo;every action leaves
        a receipt&rsquo; is becoming law. Source: Regulation (EU) 2024/1689, Art. 12 &amp; 113
        (artificialintelligenceact.eu/article/12/ and /article/113/).
      </p>
    </div>
  );
};
